import Footer from '../components/Footer';
import Contact from '../components/Contact';
import Header from '../components/Header';
import SeoHelmet from '../components/SeoHelmet';
import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { parseMarkdownLinks } from '../utils/markdownLinks';
import { faqPageSchema } from '../utils/schemaOrg';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CAPTCHA_CLIENT_KEY = process.env.REACT_APP_YANDEX_CAPTCHA_CLIENT_KEY || '';

const FAQ = () => {
  const { currentLanguage, t } = useLanguage();
  const { toast } = useToast();
  const isRU = currentLanguage === 'RU';

  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const [formData, setFormData] = useState({ name: '', email: '', question: '', category_key: '' });
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  useEffect(() => {
    loadData();
    window.onCaptchaSuccess = (token) => setCaptchaToken(token);
    const script = document.createElement('script');
    script.src = 'https://smartcaptcha.yandexcloud.net/captcha.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
      delete window.onCaptchaSuccess;
    };
  }, [currentLanguage]);

  const loadData = async () => {
    try {
      const [faqRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/faqs?language=${currentLanguage}`),
        axios.get(`${API_URL}/api/faq-categories?language=${currentLanguage}`),
      ]);
      setFaqs(faqRes.data || []);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Error loading FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Top-level categories (parent_key === null), ordered
  const topCategories = useMemo(
    () => categories.filter((c) => !c.parent_key).sort((a, b) => a.order - b.order),
    [categories]
  );
  const subcategoriesByParent = useMemo(() => {
    const map = {};
    categories
      .filter((c) => c.parent_key)
      .sort((a, b) => a.order - b.order)
      .forEach((c) => {
        (map[c.parent_key] = map[c.parent_key] || []).push(c);
      });
    return map;
  }, [categories]);

  const categoryNameByKey = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.key] = c.name; });
    return m;
  }, [categories]);

  // Apply search filter
  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = faqs;
    if (activeCategory !== 'all') {
      list = list.filter((f) => f.category_key === activeCategory);
    }
    if (!q) return list;
    return list.filter(
      (f) =>
        (f.question || '').toLowerCase().includes(q) ||
        (f.answer || '').toLowerCase().includes(q)
    );
  }, [faqs, searchQuery, activeCategory]);

  // Build grouped structure: [{ category, subgroups: [{ sub, items }], uncategorizedItems }]
  const grouped = useMemo(() => {
    const result = [];
    const usedIds = new Set();

    topCategories.forEach((cat) => {
      const catFaqs = filteredFaqs.filter((f) => f.category_key === cat.key);
      if (catFaqs.length === 0) return;
      catFaqs.forEach((f) => usedIds.add(f.id));

      const subs = subcategoriesByParent[cat.key] || [];
      const subgroups = [];
      const matchedSubKeys = new Set();
      subs.forEach((sub) => {
        const items = catFaqs.filter((f) => f.subcategory_key === sub.key);
        if (items.length > 0) {
          subgroups.push({ sub, items });
          matchedSubKeys.add(sub.key);
        }
      });
      const noSubItems = catFaqs.filter(
        (f) => !f.subcategory_key || !matchedSubKeys.has(f.subcategory_key)
      );
      result.push({ category: cat, subgroups, noSubItems });
    });

    // FAQs whose category_key doesn't match any known top category
    const orphans = filteredFaqs.filter((f) => !usedIds.has(f.id));
    if (orphans.length > 0) {
      result.push({
        category: { key: '__other', name: isRU ? 'Прочие вопросы' : 'Other questions' },
        subgroups: [],
        noSubItems: orphans,
      });
    }
    return result;
  }, [filteredFaqs, topCategories, subcategoriesByParent, isRU]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
//    if (!captchaToken) {
//      toast({
//        title: isRU ? 'Ошибка' : 'Error',
//        description: isRU ? 'Пожалуйста, пройдите проверку капчи' : 'Please complete the captcha verification',
//        variant: 'destructive',
//      });
//      return;
//    }
    setSubmitting(true);
    try {
      const formPayload = new FormData();
      formPayload.append('name', formData.name);
      formPayload.append('email', formData.email);
      formPayload.append('question', formData.question);
      formPayload.append('language', currentLanguage);
      formPayload.append('captcha_token', captchaToken || '');

// Исправление 1. Передаем красивое имя основной категории
      const selectedCat = topCategories?.find(cat => cat.key === formData.category_key);
      if (selectedCat) {
        formPayload.append('category', selectedCat.name);
      } else {
        formPayload.append('category', formData.category_key || '');
      }

      // Исправление 2. Переводим технический ключ подкатегории в красивый текст
      let subcategoryText = formData.subcategory || '';
      if (formData.subcategory === 'licenzii') subcategoryText = isRU ? 'Лицензии Минпромторга' : 'Minpromtorg Licenses';
      if (formData.subcategory === 'notifikacii') subcategoryText = isRU ? 'Нотификации ФСБ' : 'FSB Notifications';
      if (formData.subcategory === 'zaklyucheniya') subcategoryText = isRU ? 'Заключения и разрешения под ключ' : 'Conclusions & Permits';
      if (formData.subcategory === 'import') subcategoryText = isRU ? 'Параллельный импорт' : 'Parallel Import';
      
      if (formData.subcategory) {
        formPayload.append('subcategory', subcategoryText);
      }

      await axios.post(`${API_URL}/api/faqs/submit-question`, formPayload, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      toast({
        title: isRU ? 'Успешно!' : 'Success!',
        description: isRU
          ? 'Ваш вопрос отправлен. Мы ответим в ближайшее время.'
          : 'Your question has been sent. We will respond shortly.',
      });
      setFormData({ name: '', email: '', question: '', category_key: '' });
      setCaptchaToken(null);
      if (window.smartCaptcha) window.smartCaptcha.reset();
    } catch (error) {
      toast({
        title: isRU ? 'Ошибка' : 'Error',
        description: error.response?.data?.detail || (isRU ? 'Не удалось отправить вопрос' : 'Failed to submit question'),
        variant: 'destructive',
      });
      setCaptchaToken(null);
      if (window.smartCaptcha) window.smartCaptcha.reset();
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAccordion = (id) => setOpenId(openId === id ? null : id);

  const seo = (
    <SeoHelmet
      title={isRU ? 'Частые вопросы — Таможня и ВЭД | ВЕЛОКС ХИНТ' : 'FAQ — Customs & Foreign Trade | VELOX HINT'}
      description={isRU ? 'Ответы на часто задаваемые вопросы о таможенном оформлении, сертификации и лицензировании.' : 'Answers to common questions about customs clearance, certification and licensing.'}
      jsonLd={faqs.length ? faqPageSchema(faqs) : undefined}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-xl text-gray-600">{t('loading') || 'Загрузка...'}</div>
        </div>
        <Contact />
        <Footer />
      </>
    );
  }

  const totalResults = filteredFaqs.length;

  return (
    <>
      {seo}
      <Header />
      <div className="min-h-screen bg-gray-50 py-16" data-testid="faq-page">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="mb-10 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {isRU ? 'Часто задаваемые вопросы' : 'Frequently Asked Questions'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {isRU ? 'Ответы на популярные вопросы о наших услугах' : 'Answers to popular questions about our services'}
            </p>
          </div>

          {/* Search */}
          <div className="mb-6 max-w-2xl mx-auto">
            <div className="relative">
              <svg className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                data-testid="faq-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isRU ? 'Поиск по вопросам и ответам...' : 'Search questions and answers...'}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm"
              />
            </div>
          </div>

          {/* Category chips */}
          <div className="mb-8 flex flex-wrap gap-2 justify-center" data-testid="faq-category-filter">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${activeCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
              data-testid="faq-cat-chip-all"
            >
              {isRU ? 'Все' : 'All'}
            </button>
            {topCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${activeCategory === cat.key ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
                data-testid={`faq-cat-chip-${cat.key}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="mb-16" data-testid="faq-results">
            {totalResults === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <p className="text-gray-500 text-lg">
                  {isRU ? 'Ничего не найдено. Попробуйте изменить запрос.' : 'No results. Try a different query.'}
                </p>
              </div>
            ) : (
              grouped.map((group) => (
                <div key={group.category.key} className="mb-10" data-testid={`faq-group-${group.category.key}`}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-orange-200">
                    {group.category.name}
                  </h2>

                  {/* Subgroups */}
                  {group.subgroups.map(({ sub, items }) => (
                    <div key={sub.key} className="mb-6">
                      <h3 className="text-lg font-semibold text-orange-600 mb-3 pl-1">{sub.name}</h3>
                      <div className="space-y-3">
                        {items.map((faq) => (
                          <FaqItem key={faq.id} faq={faq} isOpen={openId === faq.id} onToggle={toggleAccordion} />
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Items without subcategory */}
                  {group.noSubItems.length > 0 && (
                    <div className="space-y-3">
                      {group.noSubItems.map((faq) => (
                        <FaqItem key={faq.id} faq={faq} isOpen={openId === faq.id} onToggle={toggleAccordion} />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Question Form */}
          <div className="bg-white rounded-lg shadow-sm p-8" data-testid="faq-submit-form">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isRU ? 'Не нашли ответ?' : "Didn't find an answer?"}
            </h2>
            <p className="text-gray-600 mb-6">
              {isRU ? 'Задайте свой вопрос, и мы ответим вам по email' : 'Ask your question and we will answer you by email'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{isRU ? 'Ваше имя' : 'Your name'} *</label>
                  <input type="text" name="name" data-testid="faq-form-name" value={formData.name} onChange={handleInputChange} required minLength={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input type="email" name="email" data-testid="faq-form-email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
                </div>
              </div>

	      <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isRU ? 'Категория вопроса' : 'Question category'}
                </label>
		<select
                  name="category_key"
                  data-testid="faq-form-category"
                  value={formData.category_key}
                  onChange={(e) => {
                    handleInputChange(e);
                    if (formData) formData.subcategory = "";
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                >
                  <option value="">{isRU ? '— Не выбрано —' : '— Not selected —'}</option>
                  {topCategories && topCategories.map((cat) => (
                    <option key={cat.key} value={cat.key}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Умный блок вывода подкатегорий для Нетарифного регулирования */}
              {(formData?.category_key?.toLowerCase().includes('tariff') || formData?.category_key === 'nonTariff') && (
                <div className="animate-fadeIn mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-semibold text-orange-600">
                    {isRU ? 'Выберите подкатегорию услуги *' : 'Select subcategory *'}
                  </label>
                  <select
                    name="subcategory"
                    value={formData.subcategory || ""}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border-2 border-orange-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="">{isRU ? '— Выберите подкатегорию —' : '— Select subcategory —'}</option>
                    <option value="licenzii">{isRU ? 'Лицензии' : 'Licenses'}</option>
                    <option value="notifikacii">{isRU ? 'Нотификации' : 'Notifications'}</option>
                    <option value="zaklyucheniya">{isRU ? 'Заключения и разрешения' : 'Conclusions & Permits'}</option>
                    <option value="import">{isRU ? 'Параллельный импорт' : 'Parallel Import'}</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{isRU ? 'Ваш вопрос' : 'Your question'} *</label>
                <textarea name="question" data-testid="faq-form-question" value={formData.question} onChange={handleInputChange} required minLength={10} rows={5} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
              </div>

              <button
                type="submit"
                data-testid="faq-form-submit"
                className="w-full md:w-auto px-8 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white rounded-lg font-medium transition"
              >
                {submitting ? (isRU ? 'Отправка...' : 'Sending...') : (isRU ? 'Отправить вопрос' : 'Send question')}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Contact />
      <Footer />
    </>
  );
};

const FaqItem = ({ faq, isOpen, onToggle }) => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid={`faq-item-${faq.id}`}>
    <button
      onClick={() => onToggle(faq.id)}
      className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition"
      data-testid={`faq-question-${faq.id}`}
    >
      <span className="text-base md:text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
      <svg
        className={`w-6 h-6 text-orange-500 flex-shrink-0 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && (
      <div className="px-6 pb-5 pt-0 space-y-4" data-testid={`faq-answer-${faq.id}`}>
        <p className="text-gray-600 leading-relaxed whitespace-pre-line">{parseMarkdownLinks(faq.answer)}</p>
        {faq.image_url && (
          <div className="mt-4">
            <img src={faq.image_url} alt={faq.question} className="rounded-lg max-w-full h-auto shadow-md" loading="lazy" />
          </div>
        )}
        {faq.video_url && (
          <div className="mt-4">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={faq.video_url.replace('watch?v=', 'embed/')}
                title={faq.question}
                className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

export default FAQ;
